// cart-drawer is rendered inside the header-group section group, so its actual
// rendered section id is qualified (e.g. "sections--21879109-header-group__cart-drawer"),
// not the literal "cart-drawer" filename. Requesting sections/section_id with the bare
// filename renders the section OUTSIDE the group, which drops its @app blocks entirely.
function cartDrawerSectionId() {
  const wrapper = document.querySelector('[id^="shopify-section-"][id$="cart-drawer"]');
  return wrapper ? wrapper.id.replace('shopify-section-', '') : 'cart-drawer';
}

class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.setHeaderCartIconAccessibility();
    this.setupCloseListeners();

    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.get('open-preview') === 'true') {
      this.open();
    }
  }

  connectedCallback() {
    // Rendered as part of the header-group section group, whose Shopify-generated
    // wrapper (.shopify-section-group-header-group) sets z-index: 4, capping this
    // drawer's own z-index inside that stacking context. Move it to a direct child
    // of <body> so it escapes that context entirely. Guarded to avoid an infinite
    // connect/append loop, since appendChild here re-triggers connectedCallback.
    if (this.parentElement !== document.body) {
      document.body.appendChild(this);
    }
  }

  setupCloseListeners() {
    // Event Delegation für das Overlay - funktioniert auch nach Re-Rendering
    this.removeEventListener('click', this.handleOverlayClick);
    this.addEventListener('click', this.handleOverlayClick.bind(this));
  }

  handleOverlayClick(e) {
    if (e.target.id === 'CartDrawer-Overlay') {
      this.close();
    }
    
    // Handle close buttons with event delegation
    if (e.target.closest('.drawer__close, .cart-close-btn')) {
      e.preventDefault();
      e.stopPropagation();
      this.close();
    }
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink) return;
    
    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    
    setTimeout(() => {
      this.classList.add('animate', 'active');
    });

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn = this.classList.contains('is-empty')
          ? this.querySelector('.drawer__inner-empty')
          : document.getElementById('CartDrawer');
        const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
        trapFocus(containerToTrapFocusOn, focusElement);
      },
      { once: true }
    );

    document.body.classList.add('overflow-hidden');
  }

  close() {
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    this.querySelector('.drawer__inner').classList.contains('is-empty') &&
      this.querySelector('.drawer__inner').classList.remove('is-empty');
    this.productId = parsedState.id;
    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);
      sectionElement.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
      // innerHTML never executes embedded <script> tags - re-create them so an @app
      // block relying on its own inline/injected script (e.g. a per-item warranty
      // label) actually re-runs after this AJAX add-to-cart refresh.
      reinitializeScriptTags(sectionElement);
    });

    // Wichtig: Nach dem Re-Rendering bleibt der Drawer offen
    setTimeout(() => {
      this.classList.add('animate', 'active');
    });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: cartDrawerSectionId(),
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: cartDrawerSectionId(),
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);
